// 产品矩阵系统 - 产品库侧边栏组件
// v2.0 - 支持二级型号展开和独立拖拽
import React, { useState } from 'react';
import { Search, Star, Plus, Image as ImageIcon, GripVertical, ChevronDown, ChevronRight } from 'lucide-react';
import { useDraggable } from '@dnd-kit/core';
import { useProductStore } from '../stores/product-store';
import type { Product, ProductCategory, ProductModel } from '../types';
import { ProductCategoryLabels } from '../types';

interface ProductLibraryProps {
  isDarkMode?: boolean;
}

const categoryTabs: { value: ProductCategory | 'all'; label: string }[] = [
  { value: 'all', label: '全部 (All)' },
  { value: 'all_in_one', label: '一体机 (All-in-One)' },
  { value: 'product', label: '产品 (Products)' },
  { value: 'module', label: '模组 (Module)' },
];

export function ProductLibrary({ isDarkMode = false }: ProductLibraryProps) {
  const {
    products,
    filter,
    categoryStats,
    setFilter,
    toggleFavorite,
    isFavorite,
  } = useProductStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<ProductCategory | 'all'>('all');
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());

  const handleCategoryChange = (category: ProductCategory | 'all') => {
    setActiveCategory(category);
    setFilter({ category: category === 'all' ? 'all' : category });
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    setFilter({ search: query });
  };

  const toggleExpanded = (productId: string) => {
    setExpandedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  // 按分类分组产品
  const groupedProducts = products.reduce((acc, product) => {
    const category = product.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(product);
    return acc;
  }, {} as Record<ProductCategory, Product[]>);

  return (
    <div className={`h-full flex flex-col ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <h2 className={`text-lg font-semibold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Product Library
        </h2>
        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Drag products to the canvas
        </p>
      </div>

      {/* Search */}
      <div className="p-4 border-b border-gray-200">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearch}
            placeholder="Search models, specs..."
            className={`w-full pl-10 pr-4 py-2 rounded-lg text-sm border ${
              isDarkMode
                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-500'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          />
        </div>
      </div>

      {/* Category Tabs */}
      <div className="px-4 py-2 border-b border-gray-200">
        <div className="flex flex-wrap gap-1">
          {categoryTabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => handleCategoryChange(tab.value)}
              className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                activeCategory === tab.value
                  ? 'bg-blue-100 text-blue-700 border border-blue-200'
                  : isDarkMode
                  ? 'text-gray-400 hover:bg-gray-700'
                  : 'text-gray-600 hover:bg-gray-100 border border-transparent'
              }`}
            >
              {tab.label.split(' ')[0]}
            </button>
          ))}
        </div>
      </div>

      {/* Product List */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeCategory === 'all' ? (
          // 显示所有分类
          Object.entries(groupedProducts).map(([category, categoryProducts]) => (
            <div key={category} className="mb-6">
              <h3 className={`text-xs font-semibold uppercase tracking-wider mb-2 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                {ProductCategoryLabels[category as ProductCategory]} ({category.replace('_', ' ')})
              </h3>
              <div className="space-y-1">
                {categoryProducts.map((product) => (
                  <ProductItem
                    key={product.id}
                    product={product}
                    isDarkMode={isDarkMode}
                    isFavorite={isFavorite(product.id)}
                    onToggleFavorite={() => toggleFavorite(product.id)}
                    isExpanded={expandedProducts.has(product.id)}
                    onToggleExpand={() => toggleExpanded(product.id)}
                  />
                ))}
              </div>
            </div>
          ))
        ) : (
          // 显示选中分类
          <div className="space-y-1">
            {products.map((product) => (
              <ProductItem
                key={product.id}
                product={product}
                isDarkMode={isDarkMode}
                isFavorite={isFavorite(product.id)}
                onToggleFavorite={() => toggleFavorite(product.id)}
                isExpanded={expandedProducts.has(product.id)}
                onToggleExpand={() => toggleExpanded(product.id)}
              />
            ))}
          </div>
        )}

        {products.length === 0 && (
          <div className="text-center py-8">
            <ImageIcon className={`w-12 h-12 mx-auto mb-3 ${
              isDarkMode ? 'text-gray-600' : 'text-gray-300'
            }`} />
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              No products found
            </p>
          </div>
        )}
      </div>

      {/* Footer - Add New Product */}
      <div className="p-4 border-t border-gray-200">
        <button className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 border-dashed transition-colors ${
          isDarkMode
            ? 'border-gray-600 text-gray-400 hover:border-gray-500 hover:text-gray-300'
            : 'border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-600'
        }`}>
          <Plus className="w-4 h-4" />
          <span className="text-sm font-medium">Create New Product</span>
        </button>
      </div>
    </div>
  );
}

// 单个产品项组件 - 支持展开型号
function ProductItem({
  product,
  isDarkMode,
  isFavorite,
  onToggleFavorite,
  isExpanded,
  onToggleExpand,
}: {
  product: Product;
  isDarkMode: boolean;
  isFavorite: boolean;
  onToggleFavorite: () => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
}) {
  const hasModels = product.models && product.models.length > 0;
  
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: product.id,
    data: {
      type: 'product',
      product,
      model: undefined, // 拖拽整个产品时，不指定具体型号
    },
  });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        zIndex: isDragging ? 1000 : undefined,
      }
    : undefined;

  return (
    <div className="space-y-0.5">
      {/* 产品主行 */}
      <div
        ref={setNodeRef}
        style={style}
        className={`group flex items-center gap-2 p-2 rounded-lg cursor-grab active:cursor-grabbing transition-colors ${
          isDragging
            ? 'opacity-50'
            : isDarkMode
            ? 'hover:bg-gray-700'
            : 'hover:bg-gray-50'
        }`}
        {...attributes}
        {...listeners}
      >
        {/* 展开/折叠按钮 */}
        {hasModels ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand();
            }}
            onPointerDown={(e) => e.stopPropagation()}
            className={`p-0.5 rounded transition-colors ${
              isDarkMode ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-200 text-gray-500'
            }`}
          >
            {isExpanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>
        ) : (
          <div className={`opacity-0 group-hover:opacity-100 transition-opacity ${
            isDarkMode ? 'text-gray-500' : 'text-gray-400'
          }`}>
            <GripVertical className="w-4 h-4" />
          </div>
        )}

        {/* Image - 16:9 比例 */}
        <div className="w-14 h-8 rounded overflow-hidden flex-shrink-0 bg-gray-900">
          {product.image_url ? (
            <img
              src={product.image_url}
              alt={product.name}
              className="w-full h-full object-contain"
              draggable={false}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageIcon className="w-4 h-4 text-gray-600" />
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <h4 className={`font-medium text-sm truncate ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            {product.name}
          </h4>
          <p className={`text-xs truncate ${
            isDarkMode ? 'text-gray-400' : 'text-gray-500'
          }`}>
            {product.pitch_range || product.process_type || product.series_name}
          </p>
        </div>

        {/* 型号数量标识 */}
        {hasModels && (
          <span className={`text-xs px-1.5 py-0.5 rounded ${
            isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'
          }`}>
            {product.models!.length}
          </span>
        )}

        {/* Favorite Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleFavorite();
          }}
          onPointerDown={(e) => e.stopPropagation()}
          className={`p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity ${
            isFavorite
              ? 'text-yellow-500'
              : isDarkMode
              ? 'text-gray-500 hover:text-yellow-500'
              : 'text-gray-400 hover:text-yellow-500'
          }`}
        >
          <Star className={`w-4 h-4 ${isFavorite ? 'fill-current' : ''}`} />
        </button>
      </div>

      {/* 型号列表 - 展开时显示 */}
      {isExpanded && hasModels && (
        <div className={`ml-6 pl-2 border-l-2 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          {product.models!.map((model) => (
            <ModelItem
              key={model.id}
              product={product}
              model={model}
              isDarkMode={isDarkMode}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// 型号项组件 - 可独立拖拽
function ModelItem({
  product,
  model,
  isDarkMode,
}: {
  product: Product;
  model: ProductModel;
  isDarkMode: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `${product.id}-${model.id}`,
    data: {
      type: 'product',
      product,
      model, // 拖拽具体型号时，包含型号信息
    },
  });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        zIndex: isDragging ? 1000 : undefined,
      }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex items-center gap-2 py-1.5 px-2 rounded cursor-grab active:cursor-grabbing transition-colors ${
        isDragging
          ? 'opacity-50'
          : isDarkMode
          ? 'hover:bg-gray-700'
          : 'hover:bg-gray-100'
      }`}
      {...attributes}
      {...listeners}
    >
      {/* Drag Handle */}
      <div className={`opacity-0 group-hover:opacity-100 transition-opacity ${
        isDarkMode ? 'text-gray-500' : 'text-gray-400'
      }`}>
        <GripVertical className="w-3 h-3" />
      </div>

      {/* Model Info */}
      <div className="flex-1 min-w-0">
        <span className={`text-sm font-medium ${
          isDarkMode ? 'text-gray-200' : 'text-gray-800'
        }`}>
          {model.model_name}
        </span>
        {model.cabinet_size && (
          <span className={`text-xs ml-2 ${
            isDarkMode ? 'text-gray-500' : 'text-gray-400'
          }`}>
            {model.cabinet_size}
          </span>
        )}
      </div>

      {/* Default Badge */}
      {model.is_default && (
        <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-600">
          默认
        </span>
      )}
    </div>
  );
}

export default ProductLibrary;

